/**
 * Visits — **one row per visit** (Aaron, Oct 30: *"Visits is one record that
 * spans the multiple days"*; `docs/walkthrough-findings-2026-10-30.md` §1).
 *
 * A visit is the `work_items` row and its days are inside it
 * (`docs/visit-page.md` §3.1, do-not-substitute 1): a five-day install is one
 * row, a visit with no day yet is a row reading *Unscheduled*, and the status
 * is the visit's (§17.1), never a day's. The row is drawn by
 * `components/visit-rows.tsx`, the same row every record's Visits tab draws.
 *
 * The office's question about them is never "show me visit 4417"; it is "what
 * is out there this week", "what has not been booked", "what did the Okafor
 * crew not finish". Those are filter, group and sort questions, which is why
 * this screen is the shared list engine (§3.5) and almost nothing else.
 *
 * Nothing here creates or changes a visit, and there is **no New button, ever**:
 * visits are made only from an approved quote on a job (§26; walkthrough §5).
 * Scheduling books their days, the crew board moves them and the iPad closes
 * them out; a list that also edited them would be a second door into a state
 * machine that already has one.
 */
import { useNavigate } from 'react-router-dom'
import { ListScreen } from '../components/list-screen.js'
import {
  VISIT_CELLS, VISIT_COLUMN_WIDTHS, VISIT_PRIMARY, VISIT_ROW_FIELDS, visitCardLine, visitRowMark,
  visitRowMenu,
} from '../components/visit-rows.js'
import { useList } from '../lib/use-list.js'

/*
 * No `me`. Every other list screen takes it for a permission gate or a branch
 * to import into; this one creates nothing, edits nothing and imports nothing,
 * and `requirePermission('visits', 'view')` on the server plus the rail's own
 * matrix already decide who sees it at all.
 */
export function Visits() {
  /* The mark, the Visit column's two lines, the cells and the ⋯ read these whatever the columns are. */
  const list = useList('visits', { alwaysFetch: VISIT_ROW_FIELDS })
  const navigate = useNavigate()

  return (
    <ListScreen
      area="visits"
      title="Visits"
      list={list}
      columnWidths={VISIT_COLUMN_WIDTHS}
      primaryColumn={VISIT_PRIMARY}
      cardLine={visitCardLine}
      cell={VISIT_CELLS}
      /* The visit's calendar card (`list-marks.md` §2): a second card for a multi-day visit, the loop for a series. */
      mark={{ render: (row) => visitRowMark(row) }}
      rowMenu={(row) => visitRowMenu(row, navigate)}
      /* A row opens its visit — the work item, never a day (Aaron, Oct 30). */
      onRowClick={(row) => navigate(`/visits/${String(row.id)}`)}
      emptyTitle="Nothing scheduled"
      emptyBody="A visit is one crew at one property on one day. Scheduling an approved job's work is what makes them."
      /*
       * No `importOrgUnitId`, so no Import button. A spreadsheet of visits is
       * a spreadsheet that bypasses the approved quote that makes them, the
       * capacity, the equipment exclusion constraint and the reservations a
       * booked day creates — every guarantee §6.5 and addendum §A.1 make lives
       * in the scheduler.
       */
    />
  )
}
